import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, CircleCheck, TriangleAlert, Info, CircleAlert } from 'lucide-react'
import type { Notification } from './notificationsData'

interface NotificationPanelProps {
  isOpen?: boolean
  open?: boolean
  onClose: () => void
  notifications: Notification[]
  onMarkAsRead?: (id: string, actionUrl?: string | null) => void
  onMarkAllAsRead?: () => void
}

export function NotificationPanel({
  isOpen,
  open,
  onClose,
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
}: NotificationPanelProps) {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all')

  const isPanelOpen = isOpen ?? open ?? false
  if (!isPanelOpen) return null

  const safeNotifications = Array.isArray(notifications) ? notifications : []

  const filteredNotifications = safeNotifications.filter(n => {
    if (activeTab === 'unread') return !(n.isRead ?? (n as any).read)
    return true
  })

  const unreadCount = safeNotifications.filter(n => !(n.isRead ?? (n as any).read)).length

  const getStatusIcon = (n: Notification) => {
    switch (n.type) {
      case 'FEE':
      case 'ATTENDANCE':
        return (
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#FEE2E2] text-[#DC2626] shrink-0 shadow-xs">
            <TriangleAlert className="h-3.5 w-3.5 text-[#DC2626] stroke-[2]" />
          </div>
        )
      case 'NOTICE':
      case 'COMMUNICATION':
      case 'GENERAL':
      default:
        return (
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#CCFBF1] text-[#0F766E] shrink-0 shadow-xs">
            <Info className="h-3.5 w-3.5 text-[#0F766E] stroke-[2]" />
          </div>
        )
    }
  }

  return (
    <>
      {/* Semi-transparent dark backdrop */}
      <div 
        className="fixed inset-x-0 bottom-0 top-[72px] bg-black/40 z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Right slide-out panel */}
      <div 
        className="fixed right-0 top-[72px] bottom-0 z-50 w-[440px] h-[calc(100vh-72px)] shadow-2xl flex flex-col font-['Inter'] transition-transform duration-300 ease-in-out transform translate-x-0 bg-[#F8FAFA]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-dashed border-[#E1E3E3]">
          <div className="flex items-center gap-2">
            <span className="font-bold text-[18px] text-[#0f565d]">Notifications</span>
            {unreadCount > 0 && (
              <span className="bg-[#0f565d] text-white text-[12px] font-semibold rounded-full px-2.5 py-0.5">
                {unreadCount} New
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation()
                onMarkAllAsRead?.()
              }}
              className="text-[13px] font-semibold text-[#0f565d] hover:underline cursor-pointer"
            >
              Mark all as read
            </button>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-slate-100 text-slate-500 transition-colors cursor-pointer"
              aria-label="Close panel"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-dashed border-[#E1E3E3] bg-[#F8FAFA] px-6 py-3 gap-2 overflow-x-auto">
          {(['all', 'unread'] as const).map(tab => {
            const isActive = activeTab === tab
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`rounded-full px-4 py-1.5 text-[13px] font-semibold transition-all capitalize cursor-pointer ${
                  isActive
                    ? 'bg-[#0f565d] text-white shadow-xs'
                    : 'text-[#595959] hover:bg-[#E1E3E3]/40'
                }`}
              >
                {tab}
              </button>
            )
          })}
        </div>

        {/* Scrollable Notification List */}
        <div className="flex-1 overflow-auto bg-[#F8FAFA]">
          {filteredNotifications.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              No new notifications
            </div>
          ) : (
            filteredNotifications.map(n => (
              <div
                key={n.id}
                onClick={() => {
                  onMarkAsRead?.(n.id, n.actionUrl)
                  if (n.actionUrl) {
                    navigate(n.actionUrl)
                  }
                }}
                className="w-full py-4 pr-6 pl-4 flex gap-3 transition-colors relative hover:bg-[#F8FAFA]/60 cursor-pointer border-b border-[#E5E7EB]"
                style={{ backgroundColor: !n.isRead ? '#F4F8F7' : 'transparent', minHeight: '110px' }}
              >
                {/* Unread indicator dot */}
                {!n.isRead && (
                  <span 
                    className="absolute left-[8px] top-1/2 -translate-y-1/2 h-[6px] w-[6px] rounded-full bg-[#0f565d]" 
                  />
                )}

                {/* Status Icon */}
                <div className="flex items-start pt-1 select-none shrink-0 justify-center">
                  {getStatusIcon(n)}
                </div>

                {/* Middle Column: Title & Body */}
                <div className="flex-1 min-w-0 pr-2 space-y-1">
                  <h4 className="text-[15px] font-bold text-[#191C1D] leading-tight truncate">
                    {n.title}
                  </h4>
                  <p className="text-[12.5px] text-[#475569] leading-relaxed break-words line-clamp-2">
                    {n.body}
                  </p>
                </div>

                {/* Right Column: Time */}
                <div className="flex flex-col items-end justify-between shrink-0 self-stretch min-h-[75px]">
                  <span className="text-[11px] font-medium text-[#64748B] whitespace-nowrap">
                    {n.createdAt ? new Date(n.createdAt).toLocaleDateString() : ''}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-5 flex items-center justify-center border-t border-[#E5E7EB] bg-[#F2F4F4] shrink-0">
          <button
            type="button"
            onClick={() => {
              onClose()
              navigate('/notification-center')
            }}
            className="w-full h-11 text-sm font-semibold rounded-xl bg-[#0f565d] hover:bg-[#0b4349] text-white shadow-xs flex items-center justify-center transition-colors cursor-pointer"
          >
            Open Notification Center
          </button>
        </div>
      </div>
    </>
  )
}
